import { useEffect, useState } from 'react'
import { settingsAPI, adminAPI } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

/* Setup-panels — Security-toggles (registratie/publieke modus/funnel) en
   kassa-sync. Dit zijn géén tijdens-LAN-acties: je zet ze aan/uit vóór of
   tussen LAN's. Elke toggle is een op zich staande <CardContent> — omhul
   met <Card>. Gebruikt als tab-content in AdminPage → Setup-sectie. */

export function RegistratieToggle() {
  const [aan, setAan]     = useState(null)
  const [bezig, setBezig] = useState(false)
  const [fout, setFout]   = useState(null)

  useEffect(() => {
    settingsAPI.getSecurity()
      .then(r => setAan(!!r.data?.registration_enabled))
      .catch(e => setFout(e.response?.data?.error ?? e.message))
  }, [])

  const wissel = async () => {
    setBezig(true); setFout(null)
    try {
      const nieuw = !aan
      await settingsAPI.setSecurity(nieuw)
      setAan(nieuw)
    } catch (e) { setFout(e.response?.data?.error ?? e.message) }
    finally { setBezig(false) }
  }

  return (
    <CardContent className="p-4 flex items-start gap-3 flex-wrap">
      <div className="flex-1 min-w-[15rem]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Zelfregistratie
        </div>
        <div className="font-semibold text-sm">
          {aan === null ? 'Laden…' : (aan ? 'AAN — iedereen kan zichzelf aanmaken' : 'UIT — alleen import of admin')}
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          Standaard uit. Deelnemers komen uit de kassa-import. Zet alleen
          tijdelijk aan als je een externe deelnemer laat inschrijven.
        </p>
        {fout && <div className="text-xs text-signal mt-1">{fout}</div>}
      </div>
      <Button size="sm" variant={aan ? 'default' : 'outline'} disabled={bezig || aan === null} onClick={wissel}>
        {bezig ? 'Bezig…' : (aan ? 'Uitzetten' : 'Aanzetten')}
      </Button>
    </CardContent>
  )
}

export function PubliekeModusToggle() {
  const [aan, setAan]     = useState(null)
  const [bezig, setBezig] = useState(false)
  const [fout, setFout]   = useState(null)

  useEffect(() => {
    settingsAPI.getPublicMode()
      .then(r => setAan(!!r.data?.public_mode))
      .catch(e => setFout(e.response?.data?.error ?? e.message))
  }, [])

  const wissel = async () => {
    setBezig(true); setFout(null)
    try {
      const nieuw = !aan
      await settingsAPI.setPublicMode(nieuw)
      setAan(nieuw)
    } catch (e) { setFout(e.response?.data?.error ?? e.message) }
    finally { setBezig(false) }
  }

  return (
    <CardContent className="p-4 flex items-start gap-3 flex-wrap">
      <div className="flex-1 min-w-[15rem]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Publieke modus
        </div>
        <div className="font-semibold text-sm">
          {aan === null ? 'Laden…' : (aan ? 'AAN — banner zichtbaar, testfuncties uit' : 'UIT — LAN-modus')}
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          Zet aan zolang je de site via Tailscale Funnel of een andere tunnel
          openzet. De banner verschijnt automatisch als het adres eindigt op
          <code className="mx-1 text-acid">.ts.net</code>, ook zonder deze toggle.
        </p>
        {fout && <div className="text-xs text-signal mt-1">{fout}</div>}
      </div>
      <Button size="sm" variant={aan ? 'default' : 'outline'} disabled={bezig || aan === null} onClick={wissel}>
        {bezig ? 'Bezig…' : (aan ? 'Uitzetten' : 'Aanzetten')}
      </Button>
    </CardContent>
  )
}

export function FunnelToggle() {
  const [status, setStatus] = useState(null)
  const [bezig, setBezig]   = useState(false)
  const [fout, setFout]     = useState(null)
  const [oncontact, setOff] = useState(false)

  const laad = () => {
    adminAPI.getFunnelStatus()
      .then(r => {
        if (!r.data?.beschikbaar) { setOff(true); return }
        setStatus({ aan: r.data.aan, url: r.data.url, target: r.data.target })
      })
      .catch(e => setFout(e.response?.data?.error ?? e.message))
  }
  useEffect(() => { laad() }, [])

  const wissel = async () => {
    if (!status) return
    if (!status.aan && !window.confirm(
      'Funnel aanzetten? De server wordt bereikbaar vanaf het internet.'
    )) return
    setBezig(true); setFout(null)
    try {
      const r = status.aan ? await adminAPI.funnelOff() : await adminAPI.funnelOn()
      setStatus({ aan: r.data.aan, url: r.data.url, target: r.data.target })
    } catch (e) {
      setFout(e.response?.data?.error ?? e.message)
    } finally { setBezig(false) }
  }

  if (oncontact) {
    return (
      <CardContent className="p-4 flex items-start gap-3">
        <div className="flex-1 min-w-[15rem]">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Tailscale Funnel
          </div>
          <div className="font-semibold text-sm text-muted-foreground">Niet bedienbaar vanuit deze app</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            De tailscale-socket of -binary is niet gemount in de container.
            Zet aan/uit via de terminal met <code className="text-acid">tailscale funnel --https=443 off</code>.
          </p>
        </div>
      </CardContent>
    )
  }

  return (
    <CardContent className="p-4 flex items-start gap-3 flex-wrap">
      <div className="flex-1 min-w-[15rem]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Tailscale Funnel
        </div>
        <div className={`font-semibold text-sm ${status?.aan ? 'text-live' : ''}`}>
          {status === null ? 'Laden…'
            : status.aan ? `AAN — bereikbaar op internet`
            : 'UIT — alleen bereikbaar op tailnet en LAN'}
        </div>
        {status?.url && (
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed break-all">
            {status.url}
          </p>
        )}
        {fout && <div className="text-xs text-signal mt-1">{fout}</div>}
      </div>
      <Button size="sm" variant={status?.aan ? 'default' : 'outline'} disabled={bezig || status === null} onClick={wissel}>
        {bezig ? 'Bezig…' : (status?.aan ? 'Uitzetten' : 'Aanzetten')}
      </Button>
    </CardContent>
  )
}

/* Aparte Funnel voor Pterodactyl op poort 10000. Onafhankelijke toggle
   zodat je Pterodactyl-UI remote kan bereiken zonder Tailscale op de
   client — bv. vanaf een werk-PC waar je geen Tailscale mag installeren.
   URL wordt dan https://<tailnet>.ts.net:10000. */
export function PterodactylFunnelToggle() {
  const [status, setStatus] = useState(null)
  const [bezig, setBezig]   = useState(false)
  const [fout, setFout]     = useState(null)
  const [oncontact, setOff] = useState(false)

  const laad = () => {
    adminAPI.getPterodactylFunnelStatus()
      .then(r => {
        if (!r.data?.beschikbaar) { setOff(true); return }
        setStatus({ aan: r.data.aan, url: r.data.url, target: r.data.target })
      })
      .catch(e => setFout(e.response?.data?.error ?? e.message))
  }
  useEffect(() => { laad() }, [])

  const wissel = async () => {
    if (!status) return
    if (!status.aan && !window.confirm(
      'Pterodactyl-Funnel aanzetten? De game-panel-UI wordt bereikbaar vanaf het internet.'
    )) return
    setBezig(true); setFout(null)
    try {
      const r = status.aan
        ? await adminAPI.pterodactylFunnelOff()
        : await adminAPI.pterodactylFunnelOn()
      setStatus({ aan: r.data.aan, url: r.data.url, target: r.data.target })
    } catch (e) {
      setFout(e.response?.data?.error ?? e.message)
    } finally { setBezig(false) }
  }

  if (oncontact) {
    return (
      <CardContent className="p-4 flex items-start gap-3">
        <div className="flex-1 min-w-[15rem]">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Pterodactyl-Funnel
          </div>
          <div className="font-semibold text-sm text-muted-foreground">Niet bedienbaar vanuit deze app</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Tailscale-binary of -socket ontbreekt in de container.
          </p>
        </div>
      </CardContent>
    )
  }

  return (
    <CardContent className="p-4 flex items-start gap-3 flex-wrap">
      <div className="flex-1 min-w-[15rem]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Pterodactyl-Funnel (poort 10000)
        </div>
        <div className={`font-semibold text-sm ${status?.aan ? 'text-live' : ''}`}>
          {status === null ? 'Laden…'
            : status.aan ? 'AAN — game-panel remote bereikbaar'
            : 'UIT — alleen op tailnet en LAN'}
        </div>
        {status?.url && (
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed break-all">
            {status.url}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          Voor remote zonder Tailscale op de client — bv. werk-PC. Zet uit
          zodra je klaar bent; superadmin-login blijft de enige toegang.
        </p>
        {fout && <div className="text-xs text-signal mt-1">{fout}</div>}
      </div>
      <Button size="sm" variant={status?.aan ? 'default' : 'outline'} disabled={bezig || status === null} onClick={wissel}>
        {bezig ? 'Bezig…' : (status?.aan ? 'Uitzetten' : 'Aanzetten')}
      </Button>
    </CardContent>
  )
}

import KassaSyncButton from '@/components/kassa/KassaSyncButton'
import KassaAdminStatus from '@/components/kassa/KassaAdminStatus'
import KassaUserPushCard from '@/components/kassa/KassaUserPushCard'
import FailedLoginsCard from '@/components/FailedLoginsCard'

/* Security-tab in Setup: registratie / publieke modus / funnel + failed-logins. */
export function SecurityTabContent() {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
        <Card style={{ borderLeft: '2px solid #FFB57A' }}><RegistratieToggle /></Card>
        <Card style={{ borderLeft: '2px solid #C77BFF' }}><PubliekeModusToggle /></Card>
        <Card style={{ borderLeft: '2px solid #61E294' }}><FunnelToggle /></Card>
        <Card style={{ borderLeft: '2px solid #61E294' }}><PterodactylFunnelToggle /></Card>
      </div>
      <FailedLoginsCard />
    </div>
  )
}

/* Kassa-sync tab in Setup: alle ClubKassa-koppelings-cards + externe admin-link. */
export function KassaSyncTabContent() {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 lg:grid-cols-3">
        <KassaAdminStatus />
        <KassaSyncButton />
        <KassaUserPushCard />
      </div>
    </div>
  )
}
